import { computed, onBeforeUnmount, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { PredictionContractListComplexContract } from '@/types/prediction.types';
import { expirationOptionKey } from '@/utils/forecastExExpirations';
import { getTargetPriceState } from '@/utils/targetPrice';
import { isNotYetStarted } from '@/utils/intradayContracts';
import { isMarketClosed } from '@/utils/marketClosed';

const TARGET_PRICE_POLL_MS = 5_000;

export function useTargetPricePolling(
  contracts: MaybeRefOrGetter<PredictionContractListComplexContract[]>,
  selectedExpiration: MaybeRefOrGetter<string>,
  refetch: () => Promise<unknown>,
) {
  const selectedContracts = computed(() => {
    const selected = toValue(selectedExpiration);
    return selected ? toValue(contracts).filter((contract) => expirationOptionKey(contract) === selected) : [];
  });
  const handlesSelectionRefresh = computed(() =>
    selectedContracts.value.some((contract) => getTargetPriceState(contract) !== undefined),
  );
  const pendingVenues = computed(() =>
    selectedContracts.value
      .filter((contract) => getTargetPriceState(contract) === 'pending')
      .flatMap((contract) => contract.contracts),
  );
  const pollKey = computed(() =>
    pendingVenues.value.length
      ? JSON.stringify([
          toValue(selectedExpiration),
          pendingVenues.value.map((venue) => [
            venue.conid_yes,
            venue.exchange,
            venue.start_trade_time,
            venue.last_trade_time,
          ]),
        ])
      : '',
  );
  let timer: ReturnType<typeof setTimeout> | undefined;
  let generation = 0;
  let inFlight = false;

  function stop() {
    generation++;
    clearTimeout(timer);
    timer = undefined;
  }

  function schedule() {
    clearTimeout(timer);
    if (!pollKey.value || !pendingVenues.value.some((venue) => !isMarketClosed(venue.last_trade_time))) return;
    const currentGeneration = generation;
    timer = setTimeout(async () => {
      timer = undefined;
      if (currentGeneration !== generation) return;
      const hasOpenPendingTarget = pendingVenues.value.some(
        (venue) => !isNotYetStarted(venue.start_trade_time) && !isMarketClosed(venue.last_trade_time),
      );
      if (!inFlight && hasOpenPendingTarget) {
        inFlight = true;
        await Promise.resolve()
          .then(refetch)
          .catch(() => undefined);
        inFlight = false;
      }
      if (currentGeneration === generation) schedule();
    }, TARGET_PRICE_POLL_MS);
  }

  watch(
    pollKey,
    () => {
      stop();
      schedule();
    },
    { immediate: true },
  );
  onBeforeUnmount(stop);

  return { handlesSelectionRefresh };
}
